import {
  InterventionRecipe,
  ComposableBlock,
  createDefaultRecipeForDomain,
  assignRecipeToClient,
  generateBlockId,
  validateRecipe,
} from '../sync/recipe-types';
import { BehavioralDomainId } from '../sync/target-types';

export interface AssignVoiceMemoParams {
  patientId: string;
  patientName: string;
  coachId: string;
  coachName: string;
  domainId: string;
  memoTitle: string;
  transcript?: string;
  mediaUrl: string;
  storagePath: string;
  durationSeconds: number;
  existingRecipe?: InterventionRecipe;
}

/**
 * Attaches or replaces a clinician voice memo SupportBlock inside an InterventionRecipe,
 * ensuring valid schema compliance, clinician attribution, and client assignment.
 */
export function attachVoiceMemoToRecipe(
  params: AssignVoiceMemoParams
): InterventionRecipe {
  const {
    patientId,
    patientName,
    coachId,
    coachName,
    domainId,
    memoTitle,
    transcript = '',
    mediaUrl,
    storagePath,
    durationSeconds,
    existingRecipe,
  } = params;

  // 1. Establish base recipe (existing or default for behavioral domain)
  const baseRecipe: InterventionRecipe =
    existingRecipe ||
    createDefaultRecipeForDomain(
      domainId as BehavioralDomainId,
      coachId,
      coachName
    );

  // 2. Build the new SupportBlock
  const voiceBlock: ComposableBlock = {
    id: generateBlockId('sup'),
    type: 'support',
    title: 'Therapist Supportive Voice Memo',
    instructions: 'Listen to your clinician’s personalized supportive audio message.',
    supportConfig: {
      variant: 'voice_video_note',
      mediaUrl,
      mediaType: 'audio',
      mediaTitle: memoTitle,
      transcript,
      durationSeconds,
      storagePath,
      sponsorPhoneNumber: '',
    },
  };

  // 3. Update existing voice support block or append
  const updatedBlocks = [...(baseRecipe.blocks || [])];
  const existingSupportIdx = updatedBlocks.findIndex(
    (b) => b.type === 'support' && b.supportConfig?.variant === 'voice_video_note'
  );

  if (existingSupportIdx >= 0) {
    updatedBlocks[existingSupportIdx] = voiceBlock;
  } else {
    updatedBlocks.push(voiceBlock);
  }

  // 4. Assign recipe to client with zero-knowledge envelope fields
  const updatedRecipe = assignRecipeToClient(
    {
      ...baseRecipe,
      blocks: updatedBlocks,
      updatedAt: new Date().toISOString(),
    },
    patientId,
    patientName,
    coachId,
    coachName
  );

  // 5. Schema verification
  const validation = validateRecipe(updatedRecipe);
  if (!validation.valid) {
    throw new Error(`Failed to validate recipe with attached voice memo: ${validation.errors.join(', ')}`);
  }

  return updatedRecipe;
}
